import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { ConfirmationModal, Typography, Box, LoadingText } from 'seagull';
import { ProgressBar } from 'seagull/components/ProgressBar/ProgressBar';
import { getTestIds } from 'seagull/utils/TestIds';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import styles from './BulkBillingModal.module.scss';
import { BulkBillingResultsDetails } from './BulkBillingResultsDetails/BulkBillingResultsDetails';
import { FileDetails } from './FileDetails/FileDetails';
import { useBulkBillClients } from './hooks/useBulkBillClients';
import { useUploadBulkBilling } from './hooks/useUploadBulkBilling';
import { UploadContainer } from './UploadContainer/UploadContainer';
import { ValidationErrors } from './ValidationErrors/ValidationErrors';
export const GetBulkBillingModalTID = (parentTestId)=>getTestIds(parentTestId);
export const BulkBillingModal = (param)=>{
    let { dismissModal } = param;
    const testIds = useMemo(()=>GetBulkBillingModalTID(), []);
    const { BulkBillingModalTestIds } = testIds;
    const { t: texts } = useLocaleKeys('pages.globalBillingHub');
    const { t: loadingTexts } = useLocaleKeys('components.loadingIndicator');
    const { t: commonTexts } = useLocaleKeys('common');
    const { wasFileSelected, uploadCsv, progress: uploadProgress, validationErrors, isLoading: isLoadingUploadCsv, fileDetails, reset: resetUploadCsv, bulkUploadId } = useUploadBulkBilling();
    const { billClients, progress: billProgress, isLoading: isLoadingBillClients, wasBillClientsClicked, reset: resetBulkBillClients, billResults } = useBulkBillClients({
        onSuccess: dismissModal,
        onError: dismissModal,
        testIds: {
            SuccessToast: BulkBillingModalTestIds.SuccessToast(),
            ErrorToast: BulkBillingModalTestIds.ErrorToast()
        }
    });
    const isLoading = isLoadingUploadCsv || isLoadingBillClients;
    const isBillingInProgress = isLoadingBillClients || !!billProgress;
    const isBillingDoneWithPartialFailure = !!billResults && billResults.type === 'failed';
    const progress = billProgress || uploadProgress;
    const { logBI } = useStoreActions();
    const [uploadError, setUploadError] = useState();
    const continueBilling = ()=>{
        billClients(bulkUploadId);
    };
    useEffect(()=>{
        if (wasBillClientsClicked && !wasFileSelected) {
            return setUploadError('Uploading a file is required to continue.');
        }
        setUploadError(undefined);
    }, [
        wasFileSelected,
        wasBillClientsClicked
    ]);
    const resetModal = ()=>{
        resetBulkBillClients();
        resetUploadCsv();
        setUploadError(undefined);
    };
    const uploadFile = (file)=>{
        if (file.type !== 'text/csv') {
            logBI(18701, {
                fileType: file.type
            });
            return setUploadError('Invalid file format');
        }
        uploadCsv(file);
    };
    const getPrimaryButton = ()=>{
        if (isBillingDoneWithPartialFailure) {
            return {
                label: commonTexts.buttons.close(),
                onClick: dismissModal,
                disabled: false,
                testId: BulkBillingModalTestIds.PrimaryCloseButton()
            };
        }
        if (validationErrors) {
            return {
                label: commonTexts.buttons.back(),
                onClick: resetModal,
                disabled: false,
                testId: BulkBillingModalTestIds.BackButton()
            };
        }
        if (isBillingInProgress) {
            return {
                label: commonTexts.buttons.close(),
                onClick: dismissModal,
                disabled: isBillingInProgress,
                testId: BulkBillingModalTestIds.PrimaryCloseButton()
            };
        }
        return {
            label: commonTexts.buttons.billClients(),
            onClick: continueBilling,
            disabled: isLoading,
            testId: BulkBillingModalTestIds.BillClientButton()
        };
    };
    const getSecondaryButton = ()=>{
        if (isBillingDoneWithPartialFailure) {
            return {};
        }
        if (isBillingInProgress) {
            return {};
        }
        return {
            label: commonTexts.buttons.close(),
            onClick: dismissModal,
            disabled: false,
            testId: BulkBillingModalTestIds.PrimaryCloseButton()
        };
    };
    const primaryButton = getPrimaryButton();
    const secondaryButton = getSecondaryButton();
    const getMainContent = ()=>{
        if (isBillingDoneWithPartialFailure) {
            return /*#__PURE__*/ _jsx(BulkBillingResultsDetails, {
                success: billResults.failed.succeeded,
                failure: billResults.failed.failed,
                bulkUploadId: bulkUploadId
            });
        }
        if (fileDetails) {
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(FileDetails, {
                        name: fileDetails.name,
                        lines: fileDetails.lines,
                        invoices: fileDetails.invoices,
                        invoicesCount: fileDetails.invoicesCount,
                        onRemove: resetModal
                    }),
                    validationErrors && /*#__PURE__*/ _jsx(ValidationErrors, {
                        errors: validationErrors
                    })
                ]
            });
        }
        return /*#__PURE__*/ _jsx(UploadContainer, {
            error: uploadError,
            uploadFile: uploadFile
        });
    };
    const renderProgressBar = ()=>progress ? /*#__PURE__*/ _jsxs("div", {
            className: styles.progressBarContainer,
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    testId: BulkBillingModalTestIds.ProgressBar.Label(),
                    children: [
                        isBillingInProgress ? 'Generating invoices' : 'Validating line items',
                        " (",
                        progress.completed,
                        "/",
                        progress.total,
                        ")"
                    ]
                }),
                /*#__PURE__*/ _jsx(ProgressBar, {
                    testId: BulkBillingModalTestIds.ProgressBar.Bar(),
                    completed: progress.completed,
                    total: progress.total,
                    variant: "steps"
                })
            ]
        }) : /*#__PURE__*/ _jsx(LoadingText, {
            testId: BulkBillingModalTestIds.Loader(),
            children: loadingTexts.loading()
        });
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'bulk_billing_confirm'
            },
            secondaryButton: {
                name: 'bulk_billing_cancel'
            }
        },
        width: "888px",
        title: texts.bulkBillingTitle(),
        testId: BulkBillingModalTestIds.Root(),
        primaryButtonTestId: primaryButton.testId,
        secondaryButtonTestId: secondaryButton.testId,
        textAlignCenter: false,
        primaryButtonDisabled: primaryButton.disabled,
        onPrimaryButtonClick: primaryButton.onClick,
        onCloseButtonClick: dismissModal,
        cancelButtonDisabled: isBillingInProgress,
        primaryButtonLabel: primaryButton.label,
        secondaryButtonLabel: secondaryButton.label,
        onSecondaryButtonClick: secondaryButton.onClick,
        content: /*#__PURE__*/ _jsxs(Box, {
            className: styles.content,
            children: [
                !isBillingDoneWithPartialFailure && /*#__PURE__*/ _jsxs(Box, {
                    className: styles.title,
                    testId: BulkBillingModalTestIds.Header(),
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            children: texts.importLineItemsDescription()
                        }),
                        /*#__PURE__*/ _jsx(ExternalLinkButton, {
                            href: "http://help.sayanchor.com/en/articles/11378155-billing-clients-in-bulk",
                            target: "_blank",
                            bi: {
                                name: 'bulk_billing_learn_more'
                            },
                            children: texts.learnMoreBulkBilling()
                        })
                    ]
                }),
                isLoading ? renderProgressBar() : getMainContent()
            ]
        })
    });
};
